import React, { Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getJobSettingsState,
  getSettingsState,
  getWallState,
} from '../../../store/slices/selectors'
import { jobSettingsActions } from '../../../store/slices'

import { Field } from 'formik'

import { RenderIf } from '../../../../../utils/fns'

import {
  Accordion,
  FormControl,
  Dropdown,
  FormGroup,
  FormLabel,
} from '../../../../../components/Common'
import {
  corrosionResistanceOptions,
  downloadDurationTypeOptions,
  upliftLoadDurationTypeOptions,
  helpText,
  helpImg,
} from '../../../common/json/common.json'
import { APPLICATION_TYPES } from '../../../common/constants'
import WallLengthInput from './WallLengthInput'

const JobSettings = () => {
  const dispatch = useDispatch()

  const { upliftLoadDurationType, downloadDurationType, corrosionResistance, wallLength } =
    useSelector(getJobSettingsState)

  const { country, isUSA } = useSelector(getSettingsState)
  const applicationType = useSelector((state) => getWallState(state).applicationType)

  const handleChange = (e) => {
    const { name, value } = e.target

    dispatch(jobSettingsActions.modifyCalculation({ name, value }))
  }

  const handleChangeWallLength = (value) =>
    dispatch(jobSettingsActions.modifyCalculation({ name: 'wallLength', value }))

  return (
    <Accordion title="Job Settings">
      <div className="grid grid__auto--200 grid--gap-1-2">
        <FormGroup>
          <FormLabel
            htmlFor="upliftLoadDurationType"
            popperText={helpText[country].upliftLoadDurationType}
          >
            Uplift Duration
          </FormLabel>
          <Dropdown
            name="upliftLoadDurationType"
            value={upliftLoadDurationType}
            options={upliftLoadDurationTypeOptions[country]}
            onChange={handleChange}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="uplift">Uplift ({`${isUSA ? 'ASD' : 'LSD'}`})</FormLabel>
          <Field name="uplift">
            {({ field, meta }) => (
              <FormControl
                {...field}
                addonAfter={applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? 'plf' : 'lbs'}
                onChange={handleChange}
                error={meta.error}
              />
            )}
          </Field>
        </FormGroup>
        {RenderIf(
          applicationType === APPLICATION_TYPES.PERPENDICULAR_TO_WALL,
          <Fragment>
            <FormGroup>
              <FormLabel
                htmlFor="downloadDurationType"
                popperText={helpText[country].downloadDurationType}
              >
                Download Duration
              </FormLabel>
              <Dropdown
                name="downloadDurationType"
                value={downloadDurationType}
                options={downloadDurationTypeOptions[country]}
                onChange={handleChange}
                disabled={!isUSA}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="download">Download ({`${isUSA ? 'ASD' : 'LSD'}`})</FormLabel>
              <Field name="download">
                {({ field, meta }) => (
                  <FormControl
                    {...field}
                    addonAfter={
                      applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? 'plf' : 'lbs'
                    }
                    onChange={handleChange}
                    error={meta.error}
                  />
                )}
              </Field>
            </FormGroup>
          </Fragment>,
        )}
        <FormGroup>
          <FormLabel htmlFor="f1" popperText={helpText.f1} popperImg={helpImg.f1[applicationType]}>
            F1
          </FormLabel>
          <Field name="f1">
            {({ field, meta }) => (
              <FormControl
                {...field}
                addonAfter={applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? 'plf' : 'lbs'}
                onChange={handleChange}
                error={meta.error}
              />
            )}
          </Field>
        </FormGroup>
        {RenderIf(
          applicationType !== APPLICATION_TYPES.ANGLE_TO_WALL,
          <FormGroup>
            <FormLabel
              htmlFor="f2"
              popperText={helpText.f2[applicationType] ?? helpText.f2.default}
              popperImg={helpImg.f2[applicationType] ?? helpImg.f2.default}
            >
              {applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? 'F2 Toward' : 'F2'}
            </FormLabel>
            <Field name="f2">
              {({ field, meta }) => (
                <FormControl
                  {...field}
                  addonAfter={
                    applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? 'plf' : 'lbs'
                  }
                  onChange={handleChange}
                  error={meta.error}
                />
              )}
            </Field>
          </FormGroup>,
        )}
        {RenderIf(
          applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL,
          <Fragment>
            <FormGroup>
              <FormLabel htmlFor="f2Away" popperText={helpText.f2Away} popperImg={helpImg.f2Away}>
                F2 Away
              </FormLabel>
              <Field name="f2Away">
                {({ field, meta }) => (
                  <FormControl
                    {...field}
                    addonAfter={
                      applicationType === APPLICATION_TYPES.PARALLEL_TO_WALL ? 'plf' : 'lbs'
                    }
                    onChange={handleChange}
                    error={meta.error}
                  />
                )}
              </Field>
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="wallLength">Wall Length</FormLabel>
              <WallLengthInput value={wallLength} onChange={handleChangeWallLength} />
            </FormGroup>
          </Fragment>,
        )}

        <FormGroup>
          <FormLabel htmlFor="corrosionResistance">Corrosion Resistance</FormLabel>
          <Dropdown
            name="corrosionResistance"
            value={corrosionResistance}
            options={corrosionResistanceOptions}
            onChange={handleChange}
          />
        </FormGroup>
      </div>
    </Accordion>
  )
}

export default JobSettings
